import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Spinner, Button, Typography } from "@material-tailwind/react";
import { Helmet } from "react-helmet";
import Layout from "../components/layout/Layout";
import PropertySelector from "../components/PropertySelector";
import { useProperties } from "../contexts/PropertiesContext";
import { colors } from "../utils/colors";
import ModifyAppointmentModal from "../components/modals/ModifyAppointmentModal";
import CancelAppointmentModal from "../components/modals/CancelAppointmentModal";
import { MapPinIcon, CalendarIcon, IdentificationIcon } from "@heroicons/react/24/outline";
import { trackEvent } from "../utils/analytics";

export default function Appointment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, loading, error } = useProperties();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  useEffect(() => {
    if (!loading && properties.length > 0) {
      if (id) {
        const property = properties.find(p => p.id.toString() === id);
        if (property && property.actionsNeeded.appointment) {
          setSelectedProperty(property);
          
          // Tracking de la sélection d'une propriété spécifique
          trackEvent('appointment_property_selected', {
            propertyId: property.id
          });
        } else {
          navigate("/appointment", { replace: true });
          
          // Tracking de la redirection
          trackEvent('appointment_redirect', {
            reason: property ? 'no_appointment_needed' : 'property_not_found',
            propertyId: id
          });
        }
      } else {
        const propertyNeedingAppointment = properties.find(p => p.actionsNeeded.appointment);
        if (propertyNeedingAppointment) {
          navigate(`/appointment/${propertyNeedingAppointment.id}`, { replace: true });
          
          // Tracking de la redirection automatique vers la première propriété
          trackEvent('appointment_auto_select', {
            propertyId: propertyNeedingAppointment.id
          });
        } else {
          setSelectedProperty(null);
          
          // Tracking aucun rendez-vous nécessaire
          trackEvent('appointment_none_needed', {});
        }
      }
    }
  }, [id, properties, loading, navigate]);

  const propertiesNeedingAppointment = properties.filter(p => p.actionsNeeded.appointment);

  const handlePropertyChange = (newId) => {
    // Tracking du changement de propriété
    trackEvent('appointment_change_property', {
      fromPropertyId: selectedProperty?.id,
      toPropertyId: newId
    });
    
    const property = properties.find(p => p.id.toString() === newId.toString());
    setSelectedProperty(property);
    navigate(`/appointment/${newId}`, { replace: true });
    setIframeLoading(true);
  };

  const handleOpenModifyModal = () => {
    // Tracking de l'ouverture du modal de modification
    trackEvent('appointment_open_modify_modal', {
      propertyId: selectedProperty?.id
    });
    
    setIsModifyModalOpen(true);
  };

  const handleCloseModifyModal = () => {
    // Tracking de la fermeture du modal de modification
    trackEvent('appointment_close_modify_modal', {
      propertyId: selectedProperty?.id
    });
    
    setIsModifyModalOpen(false);
  };

  const handleOpenCancelModal = () => {
    // Tracking de l'ouverture du modal d'annulation
    trackEvent('appointment_open_cancel_modal', {
      propertyId: selectedProperty?.id
    });
    
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    // Tracking de la fermeture du modal d'annulation
    trackEvent('appointment_close_cancel_modal', {
      propertyId: selectedProperty?.id
    });
    
    setIsCancelModalOpen(false);
  };

  const handleIframeLoad = () => {
    // Tracking du chargement de l'iframe
    trackEvent('appointment_iframe_loaded', {
      propertyId: selectedProperty?.id
    });
    
    setIframeLoading(false);
  };

  return (
    <Layout>
      <Helmet>
        <title>Préparer votre rendez-vous - Dimo Diagnostic</title>
        <meta name="description" content="Planifiez votre rendez-vous pour le diagnostic immobilier avec Dimo Diagnostic." />
        <link rel="canonical" href={`https://client.dimo-diagnostic.net/appointment${id ? `/${id}` : ''}`} />
      </Helmet>
      
      {loading ? (
        <div className="flex justify-center items-center h-[calc(100vh-200px)]">
          <Spinner className="h-8 w-8" style={{ color: colors.dimoPrimary }} />
        </div>
      ) : error ? (
        <Card className="mb-6 p-6 shadow-md">
          <p className="text-red-500">Une erreur s'est produite lors du chargement des données. Veuillez réessayer plus tard.</p>
        </Card>
      ) : propertiesNeedingAppointment.length === 0 ? (
        <Card className="mb-6 p-6 shadow-md">
          <p>Il n'y a actuellement aucun dossier nécessitant la planification d'un rendez-vous.</p>
        </Card>
      ) : (
        <>
          {propertiesNeedingAppointment.length > 1 && (
            <Card className="mb-6 p-6 shadow-md">
              <PropertySelector
                selectedProperty={selectedProperty ? selectedProperty.id : null}
                setSelectedProperty={handlePropertyChange}
                properties={propertiesNeedingAppointment}
                isLoading={loading}
              />
            </Card>
          )}

          {selectedProperty && (
            <>
              <Card className="mb-6 p-6 shadow-md">
                <Typography variant="h5" color="blue-gray" className="mb-4">
                  Détails du rendez-vous
                </Typography>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center">
                    <MapPinIcon className="h-5 w-5 mr-2" style={{ color: colors.dimoPrimary }} />
                    <Typography color="gray" className="font-normal">{selectedProperty.address}</Typography>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" style={{ color: colors.dimoPrimary }} />
                    <Typography color="gray" className="font-normal">{selectedProperty.appointmentText}</Typography>
                  </div>
                  <div className="flex items-center">
                    <IdentificationIcon className="h-5 w-5 mr-2" style={{ color: colors.dimoPrimary }} />
                    <Typography color="gray" className="font-normal">Dossier: {selectedProperty.id}</Typography>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <Button
                    variant="outlined"
                    color="blue-gray"
                    onClick={handleOpenCancelModal}
                    className="border-dimo-primary text-dimo-primary"
                  >
                    Annuler le RDV
                  </Button>
                  <Button
                    style={{ backgroundColor: colors.dimoPrimary }}
                    onClick={handleOpenModifyModal}
                  >
                    Modifier le RDV
                  </Button>
                </div>
                <Typography variant="small" color="gray" className="text-xs">
                  Conformément à nos conditions générales, toute annulation de votre part intervenant moins de 2 jours ouvrés avant la date du rendez-vous (48 heures), ainsi que toute situation empêchant la tenue du rendez-vous, entraînera un prélèvement d'un montant de 80 €.
                  <br/>
                  L'empreinte bancaire sera automatiquement annulée après le règlement de la prestation ou en cas de respect des conditions d'annulation.
                </Typography>
              </Card>

              <Card className="mb-6 p-6 shadow-md">
                <div className="relative w-full h-[calc(100vh-100px)]">
                  {iframeLoading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-gray-100 bg-opacity-75">
                      <Spinner className="h-8 w-8" style={{ color: colors.dimoPrimary }} />
                    </div>
                  )}
                  <iframe
                    src={selectedProperty.appointment}
                    title="Planifier un rendez-vous"
                    className="w-full h-full border-0"
                    onLoad={handleIframeLoad}
                  />
                </div>
              </Card>
            </>
          )}
        </>
      )}

      <ModifyAppointmentModal
        isOpen={isModifyModalOpen}
        onClose={handleCloseModifyModal}
        supportPhone="+33 4 82 29 76 45"
        supportEmail="planification@dimo-diagnostic.net"
      />

      <CancelAppointmentModal
        isOpen={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        propertyId={selectedProperty ? selectedProperty.id : null}
        supportPhone="+33 4 82 29 76 45"
        supportEmail="planification@dimo-diagnostic.net"
      />
    </Layout>
  );
}